import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, MenuSearchIcon } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import type { MenuProduct } from '@/entities/menu';
import type { BranchSelections, PromotionBranch } from '../model/schema';

interface BranchProductStepProps {
  branches: PromotionBranch[];
  products: MenuProduct[];
  /** `{ [branchId]: productId[] }` — the form's `selections` field. */
  value: BranchSelections;
  onChange: (next: BranchSelections) => void;
  /** Which branch's products the right pane shows. */
  activeBranchId: string | undefined;
  onSelectBranch: (branchId: string) => void;
  /** True while the active branch's stored products are still being read. */
  isLoading?: boolean | undefined;
  /** Products that are already attached to the promotion and cannot be added again. */
  disabledProductIds?: string[] | undefined;
}

/**
 * Wizard step 2, «Филиал продукта» (Figma 840-27485): the merchant's branches
 * down the left, and the ACTIVE branch's products as checkboxes on the right.
 *
 * The two panes are what make the selection per-branch. That is not a layout
 * choice — it is the shape the wire wants: `POST /promotions/batch` takes
 * `selections`, one entry per branch naming the products the promotion covers
 * THERE. The previous form offered two independent multi-selects, which could
 * only ever express a cross product, so "this product, only at that branch"
 * meant creating the campaign once per branch.
 *
 * A branch row shows its position (the design's numbered chip) and its address
 * under the name; a branch carrying at least one checked product is tinted, so
 * the merchant can see at a glance which branches the campaign touches without
 * opening each one.
 */
export function BranchProductStep({
  branches,
  products,
  value,
  onChange,
  activeBranchId,
  onSelectBranch,
  isLoading = false,
  disabledProductIds,
}: BranchProductStepProps) {
  const { t } = useTranslation('eats');
  const [search, setSearch] = useState('');

  const visibleProducts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return products;
    return products.filter((product) => product.name.toLowerCase().includes(query));
  }, [products, search]);

  const activeSelection = activeBranchId ? (value[activeBranchId] ?? []) : [];

  function toggleProduct(productId: string): void {
    if (!activeBranchId || disabledProductIds?.includes(productId)) return;
    const current = value[activeBranchId] ?? [];
    const next = current.includes(productId)
      ? current.filter((id) => id !== productId)
      : [...current, productId];
    onChange({ ...value, [activeBranchId]: next });
  }

  return (
    /* Stacked below `sm`, side by side above it.
     *
     * `useIsMobile()` routes a phone to the bottom sheet, so this is not the
     * phone case — it is the narrow WINDOW case (a tablet, a split screen, a
     * half-width browser). Two 50/50 panes at that width leave each pane too
     * narrow to read: every branch name and product truncated to «Фили…», so
     * the merchant cannot tell the rows apart to pick one. Stacking gives each
     * pane the full width and lets the step's own scroller carry the pair. */
    <div className="flex min-h-0 min-w-0 flex-1 flex-col items-stretch gap-4 sm:flex-row">
      {/* Branches. `min-h-0` + its own scroller so a long branch list scrolls
       * inside the pane rather than growing the dialog past the viewport.
       *
       * `basis-1/2 min-w-0` rather than `w-1/2`: a flex item's floor is its
       * CONTENT width, so a long branch address made the pane refuse to shrink
       * to its half and pushed the product pane out of the dialog. `min-w-0`
       * is also what lets the `truncate` on the name and address actually
       * clip — truncation inside a flex item is a no-op until the item is
       * allowed to be narrower than its text. */}
      <ul className="flex max-h-36 min-h-24 min-w-0 flex-col gap-2 overflow-y-auto rounded-2xl bg-card p-3 sm:max-h-none sm:min-h-0 sm:basis-1/2">
        {branches.map((branch, index) => {
          const isActive = branch.id === activeBranchId;
          const count = (value[branch.id] ?? []).length;
          return (
            <li key={branch.id}>
              <button
                type="button"
                onClick={() => onSelectBranch(branch.id)}
                aria-current={isActive}
                aria-label={branch.name}
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-2xl border p-[0.6875rem] text-left transition-colors',
                  'outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40',
                  isActive
                    ? 'border-border bg-muted'
                    : 'border-border bg-transparent hover:bg-muted/50',
                )}
              >
                {/* The design's numbered chip — position in the list, not an id. */}
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-card text-sm font-semibold text-foreground">
                  {index + 1}
                </span>
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="truncate text-sm font-medium text-foreground">
                    {branch.name}
                  </span>
                  {branch.address !== undefined && branch.address !== '' && (
                    <span className="truncate text-xs text-muted-foreground">
                      {branch.address}
                    </span>
                  )}
                </span>
                {/* How many products this branch carries — the only cue that a
                 * branch the merchant is not currently looking at is part of
                 * the campaign. */}
                {count > 0 && (
                  <span className="ml-auto flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                    {count}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {/* Products of the active branch. `min-w-0 basis-1/2` for the same
       * reason as the branch pane above — a long product name must not be
       * able to widen the pane past its half. */}
      <div className="flex min-h-32 min-w-0 flex-1 flex-col gap-3 rounded-2xl bg-card p-3 sm:min-h-0 sm:flex-initial sm:basis-1/2">
        <div className="relative shrink-0">
          <MenuSearchIcon
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('promotions.form.searchPlaceholder')}
            aria-label={t('promotions.form.searchPlaceholder')}
            className="h-11 w-full rounded-3xl border border-input bg-card pr-4 pl-11 text-sm text-foreground shadow-xs outline-none placeholder:text-muted-foreground focus-visible:border-input-border-focus focus-visible:ring-[3px] focus-visible:ring-ring/40"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {activeBranchId === undefined ? (
            <p className="p-3 text-sm text-muted-foreground">
              {t('promotions.form.pickBranchFirst')}
            </p>
          ) : isLoading ? (
            <p className="p-3 text-sm text-muted-foreground">
              {t('promotions.form.loadingProducts')}
            </p>
          ) : visibleProducts.length === 0 ? (
            <p className="p-3 text-sm text-muted-foreground">
              {t('promotions.form.searchEmpty')}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {visibleProducts.map((product) => {
                const id = String(product.id);
                const isDisabled =
                  disabledProductIds?.includes(String(product.id)) ?? false;
                const checked = isDisabled || activeSelection.includes(id);
                return (
                  <li key={id}>
                    {/* The whole row is the target, so the label wraps the
                     * checkbox rather than sitting beside it — a 24px box is a
                     * small hit area for a list this long. */}
                    <label
                      className={cn(
                        'flex h-8 items-center gap-1.5 px-3',
                        isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
                      )}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isDisabled}
                        onCheckedChange={() => !isDisabled && toggleProduct(id)}
                        className="size-6 rounded-lg"
                      />
                      {/* `min-w-0`: `truncate` cannot clip inside a flex item
                       * whose automatic minimum size is its own text width. */}
                      <span className="min-w-0 truncate text-sm font-medium text-foreground">
                        {product.name}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
